import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Building2, Users, ShieldCheck } from "lucide-react";
import ascotEstateLogo from "@/assets/ascot-estate-planning-logo.png";

const AWMGroup = () => {
  const groupServices = [
    {
      name: "Ascot Wealth Management",
      website: "www.ascotwm.com",
      url: "https://www.ascotwm.com",
      icon: Building2,
      description: "Comprehensive wealth management and financial planning services for individuals, families, and businesses.",
      services: [
        "Investment Advisory",
        "Portfolio Management", 
        "Financial Planning",
        "Pension Planning",
        "Insurance Services",
        "Corporate Benefits"
      ],
      highlight: "Wealth Management"
    },
    {
      name: "Ascot Estate Planning",
      website: "www.ascotep.com", 
      url: "https://www.ascotep.com",
      icon: ShieldCheck,
      description: "Specialist estate planning and tax advisory services to help preserve and transfer wealth efficiently.",
      services: [
        "Will Writing",
        "Trust Services",
        "Inheritance Tax Planning", 
        "Estate Administration",
        "Probate Services",
        "Tax Advisory"
      ],
      highlight: "Estate Planning"
    },
    {
      name: "Ascot Corporate Services",
      website: "Trading name of Ascot Estate Planning",
      url: "https://www.ascotep.com",
      icon: Users,
      description: "Corporate and business advisory services including company secretarial and compliance support.",
      services: [
        "Company Secretarial",
        "Corporate Compliance",
        "Business Advisory",
        "Corporate Structuring",
        "Regulatory Support",
        "Professional Services"
      ],
      highlight: "Corporate Services"
    }
  ];

  return (
    <div className="py-8">
      <SEO
        title="AWM Group — Wealth, Estate Planning & Investment"
        description="Cape Berkshire Asset Management is part of the AWM Group, alongside Ascot Wealth Management and Ascot Estate Planning — integrated UK financial services."
        path="/awm-group"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8 flex justify-center">
              <img 
                src="https://s3-eu-west-1.amazonaws.com/tpd/logos/5474d5c300006400057bc2c2/0x0.png" 
                alt="Ascot Wealth Management Logo" 
                className="h-48 w-auto"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">AWM Group Services</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
              A comprehensive suite of financial services through our family of companies, 
              providing complete wealth management solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Group Overview */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Family of Companies</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Cape Berkshire Asset Management is part of the wider AWM Group, offering 
              specialized services through our established brands.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {groupServices.map((service, index) => (
              <Card key={index} className="relative h-full">
                <CardHeader>
                  <div className="flex items-center justify-center mb-4 mx-auto">
                    {service.name === "Ascot Wealth Management" ? (
                      <img 
                        src="https://s3-eu-west-1.amazonaws.com/tpd/logos/5474d5c300006400057bc2c2/0x0.png" 
                        alt="Ascot Wealth Management Logo" 
                        className="h-16 w-auto"
                      />
                    ) : service.name === "Ascot Estate Planning" ? (
                      <img 
                        src={ascotEstateLogo} 
                        alt="Ascot Estate Planning Logo" 
                        className="h-16 w-auto"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-2xl text-center">{service.name}</CardTitle>
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <span className="text-sm">{service.website}</span>
                    {service.url !== "https://www.ascotep.com" && (
                      <ExternalLink className="h-4 w-4" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <CardDescription className="text-base mb-6 text-center">
                    {service.description}
                  </CardDescription>
                  
                  <div className="mb-6 flex-1">
                    <h4 className="font-semibold mb-3 text-center">Key Services:</h4>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-center text-sm text-muted-foreground">
                          <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button 
                    className="w-full mt-auto" 
                    asChild
                    variant={service.website.includes("Trading name") ? "outline" : "default"}
                  >
                    <a 
                      href={service.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      {service.website.includes("Trading name") ? "Contact Us" : "Visit Website"}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Benefits */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Integrated Service Advantage</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              By working with our group companies, you benefit from comprehensive, 
              coordinated financial services under one umbrella.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Holistic Approach</h3>
              <p className="text-sm text-muted-foreground">
                Coordinated strategy across all aspects of your financial life
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Expert Team</h3>
              <p className="text-sm text-muted-foreground">
                Specialists in each area working together for your benefit
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Regulatory Compliance</h3>
              <p className="text-sm text-muted-foreground">
                Full FCA authorization and regulatory oversight
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <ExternalLink className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Seamless Service</h3>
              <p className="text-sm text-muted-foreground">
                Smooth coordination between all group services
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Discover Our Complete Service Range
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            Contact us to learn how our integrated approach can benefit your financial planning 
            and wealth management needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <a href="/contact">Contact Our Team</a>
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary" asChild>
              <a href="/portfolios">View MPS Ranges</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AWMGroup;